import React from 'react';
import {
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { PROCESS_FLOW_CONFIG } from './poIssuesData';

/**
 * "Current Process Flow Configurations" popup — opened from the Delivery
 * Mismatch row's link. Read-only reference table.
 *
 * Columns and order mirror the source decision matrix exactly. Most cells are
 * TRUE / FALSE / blank, so they render as badges rather than words: in a matrix
 * this wide, colour is what makes a row readable at a glance, and a blank means
 * "this rule does not constrain the field" rather than "false".
 */
const COLUMNS = [
  { key: 'cmoCountry', label: 'CMO\nCountry' },
  { key: 'confirmationControlKey', label: 'Confirmation\nControl Key' },
  { key: 'deliveryWithinEU', label: 'Delivery\nWithin EU', bool: true },
  { key: 'deliveryWithinUSCanada', label: 'Delivery Within\nUS / Canada', bool: true },
  { key: 'processFlowID', label: 'Process\nFlow ID', flow: true },
  { key: 'regionCode', label: 'Region\nCode', region: true },
  { key: 'sponsorQACheckRequired', label: 'Sponsor QA\nCheck Required', bool: true },
  { key: 'shipmentRequired', label: 'Shipment\nRequired', bool: true },
];

const renderCell = (row, column, classes) => {
  const value = String(row[column.key] == null ? '' : row[column.key]).trim();

  if (!value) return <span className={classes.configBlank}>—</span>;

  if (column.bool) {
    const isTrue = value.toUpperCase() === 'TRUE';
    return (
      <span
        className={`${classes.configFlag} ${
          isTrue ? classes.configFlagTrue : classes.configFlagFalse
        }`}
      >
        {value.toUpperCase()}
      </span>
    );
  }

  if (column.flow) return <span className={classes.configFlow}>{value}</span>;
  if (column.region) return <span className={classes.configRegion}>{value}</span>;

  return value;
};

const ProcessFlowConfigDialog = ({ open, onClose, classes }) => (
  <Dialog
    open={!!open}
    onClose={onClose}
    maxWidth={false}
    classes={{ paper: classes.dialogPaper }}
  >
    <div className={classes.dialogHead}>
      <div>
        <Typography className={classes.dialogTitle}>
          Current Process Flow Configurations
        </Typography>
        <Typography className={classes.dialogSubtitle}>
          {PROCESS_FLOW_CONFIG.length} rules · a dash means the rule does not
          constrain that field
        </Typography>
      </div>
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseIcon />
      </IconButton>
    </div>
    <div className={classes.dialogBody}>
      <div className={classes.configTableWrap}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  className={`${classes.configTh} ${
                    c.bool || c.flow || c.region ? classes.configCenter : ''
                  }`}
                >
                  {/* Two-line headers keep the table from getting very wide. */}
                  {c.label.split('\n').map((line, i) => (
                    <span key={i} style={{ display: 'block' }}>
                      {line}
                    </span>
                  ))}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {PROCESS_FLOW_CONFIG.map((row, idx) => (
              <TableRow
                key={idx}
                className={`${classes.configRow} ${
                  idx % 2 ? classes.configRowAlt : ''
                }`}
              >
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    className={`${classes.configTd} ${
                      c.bool || c.flow || c.region ? classes.configCenter : ''
                    }`}
                  >
                    {renderCell(row, c, classes)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  </Dialog>
);

export default ProcessFlowConfigDialog;
